import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Search, Building2, ShieldCheck, Bot, TrendingUp, BadgeCheck, Home as HomeIcon, KeyRound, PlusCircle } from "lucide-react";
import { motion } from "framer-motion";
import PropertyCard from "@/components/PropertyCard";
import NewsSection from "@/components/NewsSection";
import HeroQuote from "@/components/HeroQuote";
import { useAuth } from "@/context/AuthContext";

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topSold, setTopSold] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/properties/top-sold").then((r) => setTopSold(r.data));
  }, []);

  const doSearch = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <div>
      {/* Hero — app style */}
      <section className="relative bg-navy-dark overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?auto=format&fit=crop&w=1600&q=70" alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/80 via-navy-dark/92 to-navy-dark" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 sm:px-8 pt-12 lg:pt-16 pb-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white" data-testid="hero-greeting">
              Namaste{user ? `, ` : " "}
              {user && <span className="text-gold">{user.name?.split(" ")[0]}</span>}
              <span className="ml-1">👋</span>
            </h1>
            <p className="mt-1.5 text-blue-100/80">India's trusted marketplace — property, cars & more. Verified & spam-free.</p>
            <HeroQuote />
          </motion.div>

          {/* Big round search bar */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 }}
            className="mt-6 flex items-center gap-2 bg-white rounded-full p-1.5 pr-2 shadow-2xl"
          >
            <div className="h-11 w-11 rounded-full bg-gold flex items-center justify-center shrink-0">
              <HomeIcon className="h-5 w-5 text-navy" />
            </div>
            <input
              data-testid="hero-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Start a search — city, town or locality…"
              className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 text-base"
            />
            <button data-testid="hero-search-btn" onClick={doSearch} className="h-11 w-11 rounded-full bg-navy hover:bg-navy-dark flex items-center justify-center transition-colors">
              <Search className="h-5 w-5 text-white" />
            </button>
          </motion.div>

          {/* Category tiles */}
          <div className="mt-5 grid grid-cols-4 gap-3">
            {[
              { key: "buy", label: "Buy", icon: Building2, to: "/properties?type=buy" },
              { key: "rent", label: "Rent", icon: KeyRound, to: "/properties?type=rent" },
              { key: "sold", label: "Sold", icon: TrendingUp, to: "/properties?type=sold" },
              { key: "post", label: "Post", icon: PlusCircle, to: "/post" },
            ].map((c, i) => (
              <motion.button
                key={c.key}
                data-testid={`cat-tile-${c.key}`}
                onClick={() => navigate(c.to)}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.12 + i * 0.05 }}
                className="flex flex-col items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl py-4 transition-colors"
              >
                <div className="h-10 w-10 rounded-xl bg-gold/15 flex items-center justify-center">
                  <c.icon className="h-5 w-5 text-gold" />
                </div>
                <span className="text-sm font-medium text-white">{c.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Recently sold near you */}
      {topSold.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-10 lg:pt-12">
          <div className="flex items-end justify-between mb-5">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-gold-dark">Market pulse</p>
              <h2 className="mt-1.5 font-display text-2xl lg:text-3xl tracking-tight font-semibold text-slate-900">Recently sold near you</h2>
            </div>
            <Button variant="outline" onClick={() => navigate("/properties?type=sold")} className="border-slate-300 hidden sm:flex" data-testid="view-sold-btn">View all</Button>
          </div>
          <div className="flex gap-5 overflow-x-auto pb-4 no-scrollbar snap-x">
            {topSold.map((p, i) => (
              <div key={p.id} className="min-w-[280px] sm:min-w-[320px] snap-start">
                <PropertyCard property={p} index={i} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Latest news — Punjab property & car market */}
      <NewsSection />

      {/* How it works / Trust */}
      <section className="bg-navy">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-gold">Why VYAPAAR</p>
            <h2 className="mt-2 font-display text-3xl lg:text-4xl tracking-tight font-semibold text-white">Property with total peace of mind</h2>
            <p className="mt-3 text-blue-100">Every owner and agent is KYC-verified. No fake listings, no spam — just genuine homes and direct, transparent contact.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: BadgeCheck, title: "KYC Verified", desc: "Owners verified via Aadhaar & PAN. NRIs via passport." },
              { icon: Search, title: "Search Smart", desc: "Filter by state, district, budget, BHK — or use the map." },
              { icon: Bot, title: "AI Assistant", desc: "Chat to instantly shortlist homes that fit your needs." },
              { icon: ShieldCheck, title: "Direct Contact", desc: "Reach verified owners & agents. Zero brokerage games." },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur"
              >
                <div className="h-12 w-12 rounded-xl bg-gold flex items-center justify-center">
                  <f.icon className="h-6 w-6 text-navy" />
                </div>
                <p className="mt-4 font-display text-lg font-semibold text-white">{f.title}</p>
                <p className="mt-1.5 text-sm text-blue-100/80 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Sold */}
      {topSold.length > 0 && (
        <section className="bg-slate-900">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-20">
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-gold">Success stories</p>
              <h2 className="mt-2 font-display text-3xl lg:text-4xl tracking-tight font-semibold text-white">Top Sold Properties</h2>
              <p className="mt-2 text-slate-400">Our most premium homes, now proud new beginnings for their owners.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {topSold.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
